import {
  ArrowLeftRight, Banknote, CircleEllipsis, CreditCard, FileText, Gift, HandCoins, MessageSquareText, NotebookPen,
  ReceiptText, Undo2, Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Button, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

import type { CurrencyBreakdown, MethodKind } from './shiftBreakdown.js';

/**
 * The close-out of a shift, drawn the same way whatever rang it up — the till
 * (.exe) or the web register. Both dialogs build this model from their own
 * data and hand it here, so a manager reads one layout: tiles at a glance, a
 * drawer card per currency (dinars and dollars are counted apart and never
 * added), the cashier's note, and the sales split by currency.
 */

/** One currency's drawer. `counted`/`difference` are null while the shift is open. */
export interface DrawerCard {
  currencyCode: string;
  float: string | null;
  received: string;
  /** Of `received`: notes taken for sales in OTHER currencies (dinars for dollar sales). */
  fromOtherSales: string | null;
  /** This currency's sales that were paid in other notes. */
  paidInOtherNotes: string | null;
  /** Change given in this currency — the register reports it apart. */
  changeGiven: string | null;
  /** Cash that went back out: refunds (register) or change & refunds (till). */
  paidOut: string | null;
  paidOutLabel: 'refunds' | 'changeAndRefunds';
  expected: string;
  counted: string | null;
  difference: number | null;
  takings: string;
  /** Of the takings: sold on account (unpaid) — why sales and cash received differ. */
  onAccount?: string | null;
  /** Of `received`: customers paying off their debt in cash (register debt payments). */
  collected?: string | null;
  /** Debt payments by card / wallet / other in this currency — not in the drawer. */
  collectedOther?: string | null;
  /** Takings that never reach the drawer: card / transfer / wallet / other / on account. */
  nonCash?: string | null;
}

/** One line of the shift's sales list. */
export interface ShiftSaleRow {
  key: string;
  number: string;
  party: string | null;
  at: string;
  currencyCode: string;
  total: string | null;
  kind: 'sale' | 'return' | 'payment' | 'voided';
  /** How it was paid, already worded (cash, card…). */
  methods: string[];
  /** The same, as method kinds — what the printed close-out groups its lines by. */
  methodKinds?: MethodKind[];
  /** A sale paid in other notes: the notes, worded ("106,000 IQD"). */
  paidWith: string | null;
  invoiceHref: string | null;
  orderHref: string | null;
}

/** Money for this view: always read as its own LTR run, so the direction marks go. */
export function useShiftMoney(): (v: string | number, ccy: string) => string {
  const { i18n } = useTranslation();
  return (v, ccy) => formatMoney(String(v), { locale: i18n.language, currency: ccy }).replace(/[\u200e\u200f]/g, '');
}

/** Today's rates FROM `base` (1 base = rate × code) — only for the overall line. */
export function useTodayRates(base: string | null): Record<string, number> {
  const [rates, setRates] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!base) return;
    let on = true;
    void api()
      .get<{ items: Array<{ code: string; rate: string }> }>('/currencies/display-rates', { params: { base } })
      .then((r) => { if (on) setRates(Object.fromEntries(r.data.items.map((x) => [x.code, Number(x.rate)]))); })
      .catch(() => { if (on) setRates({}); });
    return () => { on = false; };
  }, [base]);
  return rates;
}

/** "7 h 25 min" between two instants (the second defaults to now, for an open shift). */
export function useDuration(): (fromIso: string, toIso?: string | null) => string {
  const { t } = useTranslation('pos');
  return (fromIso, toIso) => {
    const end = toIso ? new Date(toIso).getTime() : Date.now();
    const min = Math.max(0, Math.round((end - new Date(fromIso).getTime()) / 60000));
    return t('shifts.duration', { defaultValue: '{{h}} h {{m}} min', h: Math.floor(min / 60), m: min % 60 });
  };
}

/** Over by exactly the takings that never reach the drawer — a cash sale rung up as card. */
export function overIsNonCash(c: DrawerCard): boolean {
  const n = Number(c.nonCash ?? 0);
  return c.difference !== null && c.difference > 0.004 && n > 0.004 && Math.abs(c.difference - n) < 0.005;
}

function tone(d: number): string {
  return Math.abs(d) < 0.005 ? 'text-success' : d > 0 ? 'text-warning' : 'text-danger';
}

export function ShiftTiles({ salesCount, takings, method, cards, shiftCurrency, rates, breakdown }: {
  salesCount: number;
  takings: Array<{ currencyCode: string; amount: string }>;
  method: { main: string; hint: string };
  cards: DrawerCard[];
  shiftCurrency: string;
  rates: Record<string, number>;
  /** When given, customers' debt payments get a tile of their own. */
  breakdown?: CurrencyBreakdown[];
}): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const money = useShiftMoney();
  const open = cards.some((c) => c.difference === null);
  const off = cards.filter((c) => c.difference !== null && Math.abs(c.difference) > 0.004);
  // Overall in the shift currency at today's rate — only when two or more
  // currencies are off and each has a rate: a dollars-for-dinars swap in the
  // drawer reads "over" in one and "short" in the other, and only this says
  // what really happened.
  const overall = !open && off.length > 1 && off.every((c) => c.currencyCode === shiftCurrency || rates[c.currencyCode])
    ? off.reduce((s, c) => s + (c.currencyCode === shiftCurrency ? c.difference! : c.difference! / rates[c.currencyCode]!), 0)
    : null;
  const tile = (label: string, value: JSX.Element | string, hint?: string): JSX.Element => (
    <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-bg px-3 py-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">{label}</span>
      {typeof value === 'string' ? <span dir="ltr" className="font-mono text-base font-semibold tabular-nums">{value}</span> : value}
      {hint && <span className="text-[11px] text-fg-subtle">{hint}</span>}
    </div>
  );
  // Debt payments per currency of the money that came in — not sales, so not
  // in the takings, but money the shift took and the drawer has to show.
  const debt = (breakdown ?? [])
    .map((c) => ({
      currencyCode: c.currencyCode,
      total: c.payments.reduce((acc, p) => acc + Number(p.total), 0),
      count: c.payments.reduce((acc, p) => acc + p.count, 0),
    }))
    .filter((d) => d.count > 0);
  return (
    <div className={cn('grid grid-cols-2 gap-2', debt.length > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4')}>
      {tile(t('shifts.sales', { defaultValue: 'Sales' }), formatNumber(salesCount, { locale: i18n.language }))}
      {tile(
        t('shifts.taken', { defaultValue: 'Takings' }),
        takings.filter((x) => Number(x.amount) !== 0).map((x) => money(x.amount, x.currencyCode)).join(' · ') || money(0, shiftCurrency),
      )}
      {tile(t('shifts.byMethod', { defaultValue: 'By method' }), method.main, method.hint)}
      {debt.length > 0 && tile(
        t('shifts.debtPayments', { defaultValue: 'Debt payments' }),
        <span dir="ltr" className="font-mono text-base font-semibold tabular-nums text-info">
          {debt.map((d) => money(d.total, d.currencyCode)).join(' · ')}
        </span>,
        t('shifts.paymentsSummary', { defaultValue: '{{n}} debt payments', n: formatNumber(debt.reduce((acc, d) => acc + d.count, 0), { locale: i18n.language }) }),
      )}
      {tile(
        t('shifts.result', { defaultValue: 'Result' }),
        open ? (
          <span className="font-semibold text-info">{t('shifts.stillOpen', { defaultValue: 'Still open — not counted yet' })}</span>
        ) : off.length === 0 ? (
          <span className="font-semibold text-success">{t('shifts.balanced', { defaultValue: 'Balanced' })}</span>
        ) : (
          <span className="flex flex-wrap gap-x-2 font-mono text-sm font-semibold tabular-nums">
            {off.map((c) => (
              <span key={c.currencyCode} dir="ltr" className={tone(c.difference!)}>
                {c.difference! > 0 ? '+' : '−'}{money(Math.abs(c.difference!), c.currencyCode)}
              </span>
            ))}
          </span>
        ),
        undefined,
      )}
      {overall !== null && (
        <div className="col-span-2 -mt-1 text-end text-[11px] text-fg-subtle md:col-span-4">
          {t('shifts.overall', { defaultValue: 'Overall at today\'s rate' })}:{' '}
          <span dir="ltr" className={cn('font-mono font-semibold', tone(overall))}>
            {overall > 0 ? '+' : overall < 0 ? '−' : ''}{money(Math.abs(Math.round(overall)), shiftCurrency)}
          </span>
        </div>
      )}
    </div>
  );
}

export function DrawerCards({ cards }: { cards: DrawerCard[] }): JSX.Element {
  const { t } = useTranslation('pos');
  const money = useShiftMoney();
  const row = (label: string, value: string, opts: { strong?: boolean; sub?: boolean; words?: boolean } = {}): JSX.Element => (
    <div className={cn('flex items-baseline justify-between gap-3', opts.sub ? 'ps-3 text-xs' : 'text-sm')}>
      <span className={cn('text-fg-muted', opts.sub && 'text-fg-subtle')}>{label}</span>
      {opts.words
        ? <span className="text-xs text-fg-subtle">{value}</span>
        : <span dir="ltr" className={cn('font-mono tabular-nums', opts.strong && 'text-base font-semibold text-fg')}>{value}</span>}
    </div>
  );
  const resultLabel = (d: number): string =>
    Math.abs(d) < 0.005
      ? t('shifts.balanced', { defaultValue: 'Balanced' })
      : d > 0 ? t('shifts.over', { defaultValue: 'Over' }) : t('shifts.short', { defaultValue: 'Short' });
  const positive = (v: string | null): v is string => v !== null && Number(v) > 0.004;
  return (
    <div className={cn('grid gap-3', cards.length > 1 && 'md:grid-cols-2')}>
      {cards.map((c) => (
        <div key={c.currencyCode} className="flex flex-col gap-1.5 rounded-xl border border-border bg-bg-subtle/40 p-4">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">{t('shifts.drawer', { defaultValue: 'Drawer · {{currency}}', currency: c.currencyCode })}</span>
            <span className="text-xs text-fg-muted">
              {t('shifts.salesIn', { defaultValue: '{{currency}} sales', currency: c.currencyCode })}:{' '}
              <span dir="ltr" className="font-mono">{money(c.takings, c.currencyCode)}</span>
              {positive(c.onAccount ?? null) && (
                <>
                  {' · '}{t('shifts.credit', { defaultValue: 'On account' })}:{' '}
                  <span dir="ltr" className="font-mono text-warning">{money(c.onAccount!, c.currencyCode)}</span>
                </>
              )}
            </span>
          </div>
          {c.float !== null && row(t('shifts.float', { defaultValue: 'Opening float' }), money(c.float, c.currencyCode))}
          {positive(c.nonCash ?? null) && row(
            t('shifts.notInDrawer', { defaultValue: 'Card / transfer / on account (not in the drawer)' }),
            money(c.nonCash!, c.currencyCode), { sub: true },
          )}
          {row(t('shifts.received', { defaultValue: 'Cash received' }), money(c.received, c.currencyCode))}
          {positive(c.fromOtherSales) && row(
            t('shifts.fromOtherSales', { defaultValue: 'of which for sales in other currencies' }),
            money(c.fromOtherSales, c.currencyCode), { sub: true },
          )}
          {positive(c.collected ?? null) && row(
            t('shifts.collected', { defaultValue: 'of which customers paying their debt' }),
            money(c.collected!, c.currencyCode), { sub: true },
          )}
          {positive(c.collectedOther ?? null) && row(
            t('shifts.collectedOther', { defaultValue: 'Debt paid by card / other (not in the drawer)' }),
            money(c.collectedOther!, c.currencyCode), { sub: true },
          )}
          {positive(c.paidInOtherNotes) && row(
            t('shifts.paidInOtherNotes', { defaultValue: '{{currency}} sales paid in other notes', currency: c.currencyCode }),
            money(c.paidInOtherNotes, c.currencyCode), { sub: true },
          )}
          {positive(c.changeGiven) && row(t('shifts.changeGiven', { defaultValue: 'Change given' }), `− ${money(c.changeGiven, c.currencyCode)}`)}
          {positive(c.paidOut) && row(
            c.paidOutLabel === 'refunds'
              ? t('shifts.refunds', { defaultValue: 'Cash refunds' })
              : t('shifts.paidOut', { defaultValue: 'Change & refunds paid out' }),
            `− ${money(c.paidOut, c.currencyCode)}`,
          )}
          <div className="my-1 border-t border-border" />
          {row(t('shifts.expected', { defaultValue: 'Expected cash' }), money(c.expected, c.currencyCode), { strong: true })}
          {c.counted === null
            ? row(t('shifts.counted', { defaultValue: 'Counted' }), t('shifts.notCountedYet', { defaultValue: 'not yet' }), { words: true })
            : row(t('shifts.counted', { defaultValue: 'Counted' }), money(c.counted, c.currencyCode))}
          {c.difference === null ? (
            <div className="mt-1 rounded-lg bg-info/10 px-3 py-2 text-sm font-semibold text-info">
              {t('shifts.stillOpen', { defaultValue: 'Still open — not counted yet' })}
            </div>
          ) : (
            <div className={cn(
              'mt-1 flex items-center justify-between gap-3 rounded-lg px-3 py-2',
              Math.abs(c.difference) < 0.005 ? 'bg-success/10' : c.difference > 0 ? 'bg-warning/10' : 'bg-danger/10',
            )}>
              <span className="text-sm font-semibold">{resultLabel(c.difference)}</span>
              {Math.abs(c.difference) < 0.005
                ? <Badge tone="success">✓</Badge>
                : <span dir="ltr" className={cn('font-mono text-base font-bold tabular-nums', tone(c.difference))}>{money(Math.abs(c.difference), c.currencyCode)}</span>}
            </div>
          )}
          {c.difference !== null && overIsNonCash(c) && (
            <p className="text-xs font-semibold text-danger">
              {t('shifts.overIsNonCash', { defaultValue: 'The over equals the card / transfer / on-account sales — were cash sales rung up as card?' })}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

const METHOD_ICON: Record<MethodKind, typeof Banknote> = {
  cash: Banknote, card: CreditCard, transfer: ArrowLeftRight, wallet: Wallet,
  other: CircleEllipsis, cashback: Gift, credit: NotebookPen,
};

/**
 * How the takings were paid — one block per currency: every method with its
 * count, amount and share of the takings, then the returns and the customers'
 * debt payments. "How much was card?" is answered here at a glance, in each
 * currency's own money.
 */
export function PaymentMethods({ breakdown }: { breakdown: CurrencyBreakdown[] }): JSX.Element | null {
  const { t, i18n } = useTranslation('pos');
  const money = useShiftMoney();
  const n = (v: number): string => formatNumber(v, { locale: i18n.language });
  const blocks = breakdown.filter((c) => Number(c.takings) !== 0 || c.methods.length > 0 || c.returns || c.payments.length > 0);
  if (blocks.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{t('shifts.methodsTitle', { defaultValue: 'How it was paid' })}</h3>
      <div className={cn('grid gap-3', blocks.length > 1 && 'md:grid-cols-2')}>
        {blocks.map((c) => {
          const takings = Number(c.takings);
          return (
            <div key={c.currencyCode} className="flex flex-col gap-2 rounded-xl border border-border bg-bg p-4">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold">{t('shifts.salesIn', { defaultValue: '{{currency}} sales', currency: c.currencyCode })}</span>
                <span dir="ltr" className="font-mono text-base font-bold tabular-nums">{money(c.takings, c.currencyCode)}</span>
              </div>
              {c.methods.map((m) => {
                const Icon = METHOD_ICON[m.kind];
                const share = takings > 0 ? Math.max(0, Math.min(100, (Number(m.total) / takings) * 100)) : 0;
                return (
                  <div key={m.kind} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="flex items-center gap-2 text-fg-muted">
                        <Icon className={cn('h-4 w-4', m.kind === 'credit' ? 'text-warning' : m.kind === 'cash' ? 'text-success' : 'text-info')} aria-hidden />
                        {t(`shifts.${m.kind}`, { defaultValue: m.kind })}
                        {m.count !== null && <span className="text-xs text-fg-subtle">× {n(m.count)}</span>}
                      </span>
                      <span className="flex items-baseline gap-2">
                        {share > 0 && share < 100 && <span className="text-xs text-fg-subtle">{Math.round(share)}%</span>}
                        <span dir="ltr" className="font-mono font-semibold tabular-nums">{money(m.total, c.currencyCode)}</span>
                      </span>
                    </div>
                    <div className="h-1 overflow-hidden rounded-full bg-bg-subtle">
                      <div
                        className={cn('h-full rounded-full', m.kind === 'credit' ? 'bg-warning' : m.kind === 'cash' ? 'bg-success' : 'bg-info')}
                        style={{ width: `${share}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {!c.methodsKnown && (
                <p className="text-xs text-fg-subtle">{t('shifts.methodsNotReported', { defaultValue: 'This till did not report how these were paid.' })}</p>
              )}
              {c.returns && (
                <div className="flex items-center justify-between gap-3 border-t border-border pt-2 text-sm">
                  <span className="flex items-center gap-2 text-fg-muted">
                    <Undo2 className="h-4 w-4 text-danger" aria-hidden />
                    {t('shifts.returnsRow', { defaultValue: 'Returns ({{n}})', n: n(c.returns.count) })}
                  </span>
                  <span dir="ltr" className="font-mono font-semibold tabular-nums text-danger">− {money(c.returns.total, c.currencyCode)}</span>
                </div>
              )}
              {c.payments.length > 0 && (
                <div className="flex flex-col gap-1 border-t border-border pt-2">
                  <span className="flex items-center gap-2 text-xs font-semibold text-fg-muted">
                    <HandCoins className="h-4 w-4 text-info" aria-hidden />
                    {t('shifts.debtPayments', { defaultValue: 'Debt payments' })}
                  </span>
                  {c.payments.map((p) => (
                    <div key={p.method} className="flex items-center justify-between gap-3 ps-6 text-sm">
                      <span className="text-fg-muted">
                        {t(`shifts.${p.method}`, { defaultValue: p.method })} <span className="text-xs text-fg-subtle">× {n(p.count)}</span>
                      </span>
                      <span dir="ltr" className="font-mono font-semibold tabular-nums">{money(p.total, c.currencyCode)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function NoteBox({ note }: { note: string | null | undefined }): JSX.Element | null {
  const { t } = useTranslation('pos');
  if (!note?.trim()) return null;
  return (
    <div className="flex gap-2 rounded-lg border border-dashed border-border bg-bg p-3 text-sm">
      <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
      <div>
        <div className="text-xs font-semibold text-fg-muted">{t('shifts.noteTitle', { defaultValue: 'Cashier\'s note' })}</div>
        <div className="whitespace-pre-wrap">{note}</div>
      </div>
    </div>
  );
}

export function SalesByCurrency({ rows, shiftCurrency, emptyText }: {
  rows: ShiftSaleRow[] | null;
  shiftCurrency: string;
  emptyText: string;
}): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const navigate = useNavigate();
  const money = useShiftMoney();
  const [tab, setTab] = useState<string | null>(null);
  const n = (v: number): string => formatNumber(v, { locale: i18n.language });
  const when = (iso: string): string => formatDateTime(iso, { locale: i18n.language });

  const groups = new Map<string, ShiftSaleRow[]>();
  for (const r of rows ?? []) groups.set(r.currencyCode, [...(groups.get(r.currencyCode) ?? []), r]);
  const codes = [...groups.keys()].sort((a, b) => (a === shiftCurrency ? -1 : b === shiftCurrency ? 1 : a.localeCompare(b)));
  const active = tab && groups.has(tab) ? tab : codes[0] ?? null;
  const sum = (list: ShiftSaleRow[], kind: ShiftSaleRow['kind']): number =>
    list.filter((r) => r.kind === kind).reduce((acc, r) => acc + Number(r.total ?? 0), 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('shifts.salesTitle', { defaultValue: 'Sales in this shift' })}</h3>
        {codes.length > 1 && (
          <div className="inline-flex rounded-full border border-border p-0.5" role="tablist">
            {codes.map((code) => (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={active === code}
                onClick={() => setTab(code)}
                className={cn(
                  'inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-semibold transition',
                  active === code ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg',
                )}
              >
                {code}
                <span className="opacity-80">{n(groups.get(code)!.length)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {rows === null ? (
        <Skeleton className="h-20 w-full" />
      ) : rows.length === 0 || !active ? (
        <p className="text-sm text-fg-muted">{emptyText}</p>
      ) : (() => {
        const list = groups.get(active)!;
        const returns = list.filter((r) => r.kind === 'return');
        const voided = list.filter((r) => r.kind === 'voided');
        const payments = list.filter((r) => r.kind === 'payment');
        return (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 rounded-lg bg-bg-subtle px-3 py-2 text-xs text-fg-muted">
              <span>
                {t('shifts.salesSummary', { defaultValue: '{{n}} sales', n: n(list.filter((r) => r.kind === 'sale').length) })}:{' '}
                <span dir="ltr" className="font-mono font-semibold text-fg">{money(sum(list, 'sale'), active)}</span>
              </span>
              {returns.length > 0 && (
                <span>
                  {t('shifts.returnsSummary', { defaultValue: '{{n}} returns', n: n(returns.length) })}:{' '}
                  <span dir="ltr" className="font-mono font-semibold text-danger">− {money(sum(list, 'return'), active)}</span>
                </span>
              )}
              {payments.length > 0 && (
                <span>
                  {t('shifts.paymentsSummary', { defaultValue: '{{n}} debt payments', n: n(payments.length) })}:{' '}
                  <span dir="ltr" className="font-mono font-semibold text-info">{money(sum(list, 'payment'), active)}</span>
                </span>
              )}
              {voided.length > 0 && (
                <span>{t('shifts.voidedSummary', { defaultValue: '{{n}} voided', n: n(voided.length) })}</span>
              )}
            </div>
            <ul className="flex flex-col divide-y divide-border overflow-hidden rounded-lg border border-border">
              {list.map((r) => (
                <li key={r.key} className={cn('flex items-center gap-2 px-3 py-2 text-sm', r.kind === 'voided' && 'opacity-60')}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-mono text-xs">{r.number}</span>
                    <span className="block truncate text-xs text-fg-muted">{r.party ?? '—'} · {when(r.at)}</span>
                  </span>
                  {r.methods.map((m) => <Badge key={m} tone="neutral">{m}</Badge>)}
                  {r.paidWith && (
                    <Badge tone="info">
                      {r.kind === 'return'
                        // A return handed back in other notes: what the customer got.
                        ? t('shifts.refundedWith', { defaultValue: 'refunded {{amount}}', amount: r.paidWith })
                        : t('shifts.paidWith', { defaultValue: 'paid {{amount}}', amount: r.paidWith })}
                    </Badge>
                  )}
                  {r.kind === 'return' && <Badge tone="danger">{t('shifts.kindReturn', { defaultValue: 'Return' })}</Badge>}
                  {r.kind === 'payment' && <Badge tone="info">{t('shifts.kindPayment', { defaultValue: 'Payment' })}</Badge>}
                  {r.kind === 'voided' && <Badge tone="warning">{t('shifts.voided', { defaultValue: 'Voided' })}</Badge>}
                  {r.total !== null && (
                    <span dir="ltr" className={cn('w-32 text-end font-mono tabular-nums', r.kind === 'return' && 'text-danger', r.kind === 'voided' && 'line-through')}>
                      {r.kind === 'return' ? '− ' : ''}{money(r.total, r.currencyCode)}
                    </span>
                  )}
                  {r.invoiceHref && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t('shifts.openInvoice', { defaultValue: 'Invoice' })}
                      title={t('shifts.openInvoice', { defaultValue: 'Invoice' })}
                      onClick={() => navigate(r.invoiceHref!)}
                    >
                      <ReceiptText className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                  {r.orderHref && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t('shifts.openSale', { defaultValue: 'Open' })}
                      onClick={() => navigate(r.orderHref!)}
                    >
                      <FileText className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </>
        );
      })()}
    </div>
  );
}
